import { type SelectProps } from 'antd';
import { uniq } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetModulesOptionsQuery } from '../../api/queries/getModulesOptions';
import { type ModuleFilteringRule } from '../../api/types';
import { useCompany } from '../contexts/CompanyContextManager';
import SelectField from './SelectField';
import { type CTSFinderModuleSelectFieldProps, getFinderCTSModuleOptions } from './shared';

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const CTSFinderModuleSelectField = ({
    modules: modulesFromProps,
    moduleType,
    moduleTypes,
    moduleRole,
    companyId,
    ctsSettingProps,
    forceSkipFetch = false,
    addNoneOption = false,
    setFinderApplicationModuleCondition,
    ...props
}: CTSFinderModuleSelectFieldProps) => {
    const company = useCompany(true);
    const companyIdToUse = companyId || company?.id;

    const filter = useMemo(
        (): ModuleFilteringRule => ({ moduleType, moduleTypes, moduleRole, companyId: companyIdToUse }),
        [moduleType, moduleTypes, moduleRole, companyIdToUse]
    );
    const { t } = useTranslation('moduleDetails');

    const { data, loading } = useGetModulesOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
        skip: !!modulesFromProps || forceSkipFetch,
    });

    const modules = useMemo(() => modulesFromProps || data?.modules?.items, [data?.modules?.items, modulesFromProps]);

    const options = useMemo(
        () => getFinderCTSModuleOptions(addNoneOption, modules, ctsSettingProps, t),
        [addNoneOption, ctsSettingProps, modules, t]
    );

    const onSelect = (_: any, option: any) => {
        if (setFinderApplicationModuleCondition) {
            setFinderApplicationModuleCondition(prevConditions => uniq([...prevConditions, ...option.conditions]));
        }

        return null;
    };

    const onDeselect = (value, option) => {
        if (setFinderApplicationModuleCondition) {
            setFinderApplicationModuleCondition(prevState =>
                prevState.filter(state => option.conditions.indexOf(state) === -1)
            );
        }

        return null;
    };

    return (
        <SelectField
            {...props}
            filterOption={filterOption}
            loading={!modules && loading}
            onDeselect={onDeselect}
            onSelect={onSelect}
            options={options}
            showSearch
        />
    );
};

export default CTSFinderModuleSelectField;
